import React, { useMemo } from 'react';
import LineChartView from './charts/LineChartView';
import BarChartView from './charts/BarChartView';
import ScatterChartView from './charts/ScatterChartView';
import AreaChartView from './charts/AreaChartView';
import HeatmapView from './charts/HeatmapView';
import MethodComparisonView from './charts/MethodComparisonView';
import StatisticalAnalysisView from './charts/StatisticalAnalysisView';
import ComprehensiveOverviewView from './charts/ComprehensiveOverviewView';
import SettingComparisonView from './charts/SettingComparisonView';
import RadarChartView from './charts/RadarChartView';
import { getDocCountsForLength } from '../constants';

const VisualizationContainer = ({
  viewType,
  selectedMethod,
  selectedModel,
  methodData,
  selectedLanguages,
  heatmapMetric,
  statisticalView,
  selectedMethodsForComparison,
  comparisonModel,
  experimentsMetadata
}) => {
  // Dynamically determine doc counts based on actual data length
  const docCounts = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return getDocCountsForLength(7); // default
    }
    const modelData = methodData[selectedMethod][selectedModel];
    // Find the maximum length of data across all languages
    const maxLength = Math.max(
      ...selectedLanguages.map(lang => (modelData[lang]?.length || 0))
    );
    return getDocCountsForLength(maxLength);
  }, [selectedMethod, selectedModel, methodData, selectedLanguages]);

  const prepareChartData = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return [];
    }

    const modelData = methodData[selectedMethod][selectedModel];

    return docCounts.map((count, index) => {
      const dataPoint = { documents: count };
      selectedLanguages.forEach(lang => {
        const langData = modelData[lang];
        dataPoint[lang] = langData?.[index] ?? null;
      });
      return dataPoint;
    });
  }, [selectedMethod, selectedModel, methodData, selectedLanguages, docCounts]);

  const renderVisualization = () => {
    const commonProps = {
      selectedMethod,
      selectedModel,
      methodData,
      selectedLanguages,
      chartData: prepareChartData,
      experimentsMetadata,
      docCounts
    };

    switch (viewType) {
      case 'lines':
        return <LineChartView {...commonProps} />;
      case 'bars':
        return <BarChartView {...commonProps} />;
      case 'scatter':
        return <ScatterChartView {...commonProps} />;
      case 'area':
        return <AreaChartView {...commonProps} />;
      case 'heatmap':
        return <HeatmapView {...commonProps} heatmapMetric={heatmapMetric} />;
      case 'radar':
        return <RadarChartView {...commonProps} />;
      case 'settingComparison':
        return (
          <SettingComparisonView
            {...commonProps}
            selectedMethodsForComparison={selectedMethodsForComparison}
            comparisonModel={comparisonModel}
          />
        );
      case 'methodComparison':
        return (
          <MethodComparisonView
            {...commonProps}
            selectedMethodsForComparison={selectedMethodsForComparison}
            comparisonModel={comparisonModel}
          />
        );
      case 'statistical':
        return <StatisticalAnalysisView {...commonProps} statisticalView={statisticalView} />;
      case 'comprehensive':
        return <ComprehensiveOverviewView {...commonProps} />;
      default:
        return <LineChartView {...commonProps} />;
    }
  };

  return (
    <section className="mb-6">
      {renderVisualization()}
    </section>
  );
};

export default VisualizationContainer;
